/**
 * Hotkey command ↔ display-key transposition, ported from the legacy Blueprint
 * `transposeKey` (ado-277922). Behaviour is preserved exactly, including the
 * quirks noted below, because the output feeds the ctrl+/ overlay.
 */

type TTransposeType = "os" | "user";

/**
 * Per-OS substitutions, applied to each comma-separated key in a command.
 *
 * - `"os"` normalizes an authored command to what the platform actually fires:
 *   on macOS `ctrl`/`cmd` both become `meta`; elsewhere `cmd` maps to `""`,
 *   which *drops that key entirely* (see `transposeSingleKey`).
 * - `"user"` rewrites it for display, into the glyphs a user sees on their
 *   keyboard (⌘, ⇧, →).
 *
 * A key mapping to `""` is a deletion, not a rename — that is how
 * "ctrl+s, cmd+s" collapses to a single entry per platform.
 */
type TKeySubstitutions = Record<string, string>;

const OS_KEY_MAPS: Record<
  TTransposeType,
  { default: TKeySubstitutions } & Record<string, TKeySubstitutions>
> = {
  os: {
    MacOS: { ctrl: "meta", esc: "escape", cmd: "meta" },
    default: { esc: "escape", cmd: "" },
  },
  user: {
    MacOS: {
      meta: "⌘",
      shift: "⇧",
      alt: "⌥",
      arrowright: "→",
      arrowleft: "←",
      arrowup: "↑",
      arrowdown: "↓",
      ctrl: "⌃",
      cmd: "⌘",
    },
    default: {
      arrowright: "→",
      arrowleft: "←",
      arrowup: "↑",
      arrowdown: "↓",
      cmd: "",
      meta: "",
    },
  },
};

function getOS(): string {
  const userAgent = window.navigator.userAgent;
  if (userAgent.includes("Win")) {
    return "Windows";
  } else if (userAgent.includes("Mac")) {
    return "MacOS";
  } else if (userAgent.includes("Linux")) {
    return "Linux";
  } else if (userAgent.includes("Android")) {
    return "Android";
  } else if (userAgent.includes("iOS")) {
    return "iOS";
  }
  return "Unknown";
}

function getMap(type: TTransposeType): TKeySubstitutions {
  const map = OS_KEY_MAPS[type];
  return map[getOS()] ?? map.default;
}

function transposeSingleKey(key: string, type: TTransposeType): string {
  const keys = getMap(type);
  // Substitutions match whole `+`-delimited tokens. The legacy implementation used an
  // unanchored /k/g regex, which double-applied whenever a mapping key was a substring of
  // its own replacement — "escape" became "escapeape" via the esc → escape entry. No
  // command authors an already-normalized token today, so every real command transposes
  // identically; this only stops the next one from silently mangling.
  const tokens = key.trim().split("+");
  const transposed: string[] = [];
  for (const token of tokens) {
    const substitution = keys[token];
    if (substitution === undefined) {
      transposed.push(token);
    } else if (substitution === "") {
      // An empty replacement blanks the whole key rather than just that token —
      // this is what removes the platform's non-applicable half of a
      // "ctrl+s, cmd+s" pair. Preserved from the legacy implementation.
      return "";
    } else {
      transposed.push(substitution);
    }
  }
  return transposed.join("+");
}

/**
 * Transposes a hotkey command for the current OS, de-duplicating the result.
 *
 * @param key a command, possibly comma-separated (e.g. `"ctrl+s, cmd+s"`).
 * @param type `"os"` for the keys the platform fires, `"user"` for display glyphs.
 * @returns the transposed keys, comma-space joined, with blanks and duplicates dropped.
 * @example
 * transposeKey("ctrl+s, cmd+s", "os"); // Windows → "ctrl+s"; macOS → "meta+s"
 */
export function transposeKey(key: string, type: TTransposeType = "os"): string {
  const seen = new Set<string>();
  key
    .split(",")
    .map((singleKey) => transposeSingleKey(singleKey, type))
    .filter(Boolean)
    .forEach((transposed) => seen.add(transposed));
  return Array.from(seen).join(", ");
}
